<template>
  <div :class="appShellClass" :data-locale="$i18n.locale">
    <header v-if="!isAuthRoute" class="sticky top-0 z-[120] border-b border-white/5 bg-midnight-900/70 backdrop-blur py-4">
      <div class="mx-auto flex w-full max-w-[88rem] flex-col gap-4 px-6 py-4 sm:px-8 lg:px-10 nav:flex-row nav:items-center nav:justify-between">
        <div class="flex items-center justify-between gap-3">
          <a class="flex items-center gap-3" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
            <div class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl bg-white/10 ring-1 ring-white/15">
              <img
                src="@/assets/logoDRVN.png"
                alt="DRVN logo"
                class="h-full w-full object-cover"
              />
            </div>
            <div>
              <p class="font-display text-sm uppercase tracking-[0.35em] text-steel-300">DRVN</p>
              <p class="text-xs text-steel-400">{{ $t('app.tagline') }}</p>
            </div>
          </a>
          <div class="flex items-center gap-2 nav:hidden">
            <button
              v-if="isAuthenticated"
              type="button"
              class="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-midnight-900 transition hover:bg-neon-400"
              @click="openMobileComposer"
            >
              <svg class="mr-1.5 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 5v14M5 12h14" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>Post</span>
            </button>
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-steel-200 hover:text-white"
              @click="isNavOpen = !isNavOpen"
            >
              <svg v-if="!isNavOpen" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M4 6h16M4 12h16M4 18h16" stroke-width="1.6" stroke-linecap="round" />
              </svg>
              <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M6 6l12 12M18 6l-12 12" stroke-width="1.6" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <nav class="hidden flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-steel-300 nav:flex">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-steel-200 transition hover:bg-white/10 hover:text-white"
            :aria-label="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
            :title="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
            @click="toggleTheme"
          >
            <svg v-if="isLightTheme" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="4" stroke-width="1.6" />
              <path d="M12 2.5v2.25M12 19.25v2.25M4.93 4.93l1.59 1.59M17.48 17.48l1.59 1.59M2.5 12h2.25M19.25 12h2.25M4.93 19.07l1.59-1.59M17.48 6.52l1.59-1.59" stroke-width="1.6" stroke-linecap="round" />
            </svg>
            <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <form class="relative" v-if="isAuthenticated" data-search-root @submit.prevent="handleSearch">
            <input
              v-model.trim="searchQuery"
              type="text"
              :placeholder="$t('app.searchPlaceholder')"
              class="w-44 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-steel-200 placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30 sm:w-56"
              @focus="searchActive = true"
              @input="queueSearch"
            />
            <div
              v-if="showSearchResults"
              class="absolute left-0 top-11 z-20 w-64 rounded-2xl border border-white/10 bg-midnight-900/95 p-2 text-xs text-steel-200 shadow-glow backdrop-blur"
            >
              <button
                v-for="user in searchResults"
                :key="user.handle"
                type="button"
                class="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/5"
                @click="goToUser(user.handle)"
              >
                <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    alt="User avatar"
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                </div>
                <div>
                  <div class="flex items-center gap-1.5">
                    <p class="text-sm text-white">{{ user.fullName || 'DRVN Member' }}</p>
                    <verified-badge v-if="user.verified" size="xs" />
                  </div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
                </div>
              </button>
              <p v-if="!searchResults.length && !searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                {{ $t('app.noUserFound') }}
              </p>
              <p v-if="searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                {{ $t('app.searching') }}
              </p>
            </div>
          </form>
          <a v-if="!isAuthenticated" :class="desktopNavLinkClass('/login')" :href="resolveAppHref('/login')" @click.prevent="navigateWithReload('/login')">{{ $t('nav.login') }}</a>
          <a v-if="!isAuthenticated" :class="desktopNavLinkClass('/register')" :href="resolveAppHref('/register')" @click.prevent="navigateWithReload('/register')">{{ $t('nav.register') }}</a>
          <div v-if="isAuthenticated" class="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <a :class="desktopNavLinkClass('/feed')" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M3 12.5 12 4l9 8.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M6.5 10.5V20h11V10.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>{{ $t('nav.feed') }}</span>
            </a>
            <a :class="desktopNavLinkClass('/meets')" :href="resolveAppHref('/meets')" @click.prevent="navigateWithReload('/meets')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M8 3v3M16 3v3M4 9h16" stroke-width="1.8" stroke-linecap="round" />
                <rect x="4" y="5.5" width="16" height="14.5" rx="3" stroke-width="1.8" />
              </svg>
              <span>{{ $t('nav.meets') }}</span>
            </a>
            <a :class="desktopNavLinkClass('/tournament')" :href="resolveAppHref('/tournament')" @click.prevent="navigateWithReload('/tournament')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M7 4h10v3a4 4 0 0 1-4 4h-2A4 4 0 0 1 7 7V4Z" stroke-width="1.8" stroke-linejoin="round" />
                <path d="M9 20h6M12 11v9" stroke-width="1.8" stroke-linecap="round" />
                <path d="M7 5H5a2 2 0 0 0 2 2M17 5h2a2 2 0 0 1-2 2" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>{{ $t('nav.tournament') }}</span>
            </a>
            <a :class="desktopNavLinkClass('/messages')" :href="resolveAppHref('/messages')" @click.prevent="navigateWithReload('/messages')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M5 6.5h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" stroke-width="1.8" stroke-linejoin="round" />
              </svg>
              <span>{{ $t('nav.messages') }}</span>
              <span
                v-if="messageUnreadCount"
                class="ml-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-neon-400 px-1 text-[10px] font-semibold text-midnight-900"
              >
                {{ messageUnreadCount }}
              </span>
            </a>
          </div>
          <a v-if="isAuthenticated" :class="iconActionClass('/notifications')" :href="resolveAppHref('/notifications')" @click.prevent="openNotifications">
            <span class="sr-only">{{ $t('nav.notifications') }}</span>
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span
              v-if="unreadCount"
              class="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white transform transition-transform"
              :class="showTransientBadge ? 'pop-animate' : ''"
            >
              {{ unreadCount }}
            </span>
          </a>
          <div v-if="isAuthenticated" class="relative" data-account-menu-root>
            <button
              type="button"
              class="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] p-1 pr-3 text-steel-200 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
              @click="accountMenuOpen = !accountMenuOpen"
              aria-haspopup="true"
              :aria-expanded="accountMenuOpen"
            >
              <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="displayAvatarUrl"
                :src="displayAvatarUrl"
                alt="Profile avatar"
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
                <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ initials }}</div>
              </div>
              <span class="text-[10px] font-semibold uppercase tracking-[0.22em]">{{ $t('nav.profile') }}</span>
            </button>

            <div v-if="accountMenuOpen" class="absolute right-0 mt-2 w-44 rounded-2xl border border-white/10 bg-midnight-900/95 p-2 shadow-glow">
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref(`/u/${publicProfileHandle}`)" @click.prevent="navigateWithReload(`/u/${publicProfileHandle}`)">{{ $t('nav.profile') }}</a>
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/profile')" @click.prevent="navigateWithReload('/profile')">{{ $t('nav.settings') }}</a>
              <a v-if="isAdmin" class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/admin')" @click.prevent="navigateWithReload('/admin')">{{ $t('nav.admin') }}</a>
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/report')" @click.prevent="navigateWithReload('/report')">{{ $t('nav.reportIssue') }}</a>
              <button class="block w-full text-left rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" @click="signOut">{{ $t('nav.logout') }}</button>
            </div>
          </div> 
        </nav>
      </div>
      <nav
        v-if="isNavOpen"
        class="relative z-[130] flex max-h-[calc(100vh-8rem)] flex-col gap-4 overflow-y-auto border-t border-white/5 px-6 pb-5 text-xs uppercase tracking-[0.3em] text-steel-300 nav:hidden"
      >
        <button
          type="button"
          class="mt-4 inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-steel-200 transition hover:bg-white/10 hover:text-white"
          :aria-label="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
          :title="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
          @click="toggleTheme"
        >
          <svg v-if="isLightTheme" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="4" stroke-width="1.6" />
            <path d="M12 2.5v2.25M12 19.25v2.25M4.93 4.93l1.59 1.59M17.48 17.48l1.59 1.59M2.5 12h2.25M19.25 12h2.25M4.93 19.07l1.59-1.59M17.48 6.52l1.59-1.59" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <form class="relative w-full pt-4" v-if="isAuthenticated" data-search-root @submit.prevent="handleSearch">
          <input
            ref="mobileSearchInput"
            v-model.trim="searchQuery"
            type="text"
            :placeholder="$t('app.searchPlaceholder')"
            class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-steel-200 placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            @focus="searchActive = true"
            @input="queueSearch"
          />
          <div
            v-if="showSearchResults"
            class="absolute left-0 top-11 z-20 w-full rounded-2xl border border-white/10 bg-midnight-900/95 p-2 text-xs text-steel-200 shadow-glow backdrop-blur"
          >
            <button
              v-for="user in searchResults"
              :key="user.handle"
              type="button"
              class="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/5"
              @click="goToUser(user.handle)"
            >
              <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                <img
                  v-if="user.avatar"
                  :src="user.avatar"
                  alt="User avatar"
                  class="h-full w-full object-cover"
                  referrerpolicy="no-referrer"
                  crossorigin="anonymous"
                />
              </div>
              <div>
                <div class="flex items-center justify-center gap-1.5 sm:justify-start">
                  <p class="text-sm text-white">{{ user.fullName || 'DRVN Member' }}</p>
                  <verified-badge v-if="user.verified" size="xs" />
                </div>
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
              </div>
            </button>
            <p v-if="!searchResults.length && !searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
              {{ $t('app.noUserFound') }}
            </p>
            <p v-if="searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
              {{ $t('app.searching') }}
            </p>
          </div>
        </form>
        <div v-if="!isAuthenticated" class="grid w-full gap-3 pt-2">
          <a :class="mobileNavLinkClass('/login')" :href="resolveAppHref('/login')" @click.prevent="navigateWithReload('/login')">{{ $t('nav.login') }}</a>
          <a :class="mobileNavLinkClass('/register')" :href="resolveAppHref('/register')" @click.prevent="navigateWithReload('/register')">{{ $t('nav.register') }}</a>
        </div>
        <div v-if="isAuthenticated" class="grid w-full gap-3 pt-2">
          <a :class="mobileNavLinkClass('/feed')" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
            <span>{{ $t('nav.feed') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">home</span>
          </a>
          <a :class="mobileNavLinkClass('/meets')" :href="resolveAppHref('/meets')" @click.prevent="navigateWithReload('/meets')">
            <span>{{ $t('nav.meets') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">events</span>
          </a>
          <a :class="mobileNavLinkClass('/tournament')" :href="resolveAppHref('/tournament')" @click.prevent="navigateWithReload('/tournament')">
            <span>{{ $t('nav.tournament') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">competition</span>
          </a>
          <a :class="mobileNavLinkClass('/messages')" :href="resolveAppHref('/messages')" @click.prevent="navigateWithReload('/messages')">
            <span>{{ $t('nav.messages') }}</span>
            <span
              v-if="messageUnreadCount"
              class="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-neon-400 px-1 text-[10px] font-semibold text-midnight-900"
            >
              {{ messageUnreadCount }}
            </span>
          </a>
          <a :class="mobileNavLinkClass('/notifications')" :href="resolveAppHref('/notifications')" @click.prevent="openNotifications">
            <span>{{ $t('nav.notifications') }}</span>
            <span
              v-if="unreadCount"
              class="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white transform transition-transform"
              :class="showTransientBadge ? 'pop-animate' : ''"
            >
              {{ unreadCount }}
            </span>
          </a>
          <a :class="mobileNavLinkClass(`/u/${publicProfileHandle}`)" :href="resolveAppHref(`/u/${publicProfileHandle}`)" @click.prevent="navigateWithReload(`/u/${publicProfileHandle}`)">
            <span>{{ $t('nav.profile') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">@{{ publicProfileHandle }}</span>
          </a>
        </div>
        <details v-if="isAuthenticated" class="w-full">
          <summary class="w-full cursor-pointer list-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-steel-200">
            {{ $t('nav.more') }}
          </summary>
          <div class="mt-3 max-h-[min(42vh,22rem)] space-y-4 overflow-y-auto pr-1">
            <div class="grid gap-2 rounded-[1.5rem] border border-white/10 bg-white/[0.03] p-2">
              <a
                class="flex items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-medium text-steel-100 transition hover:bg-white/[0.06] hover:text-white"
                :href="resolveAppHref('/profile')"
                @click.prevent="navigateWithReload('/profile')"
              >
                <span>{{ $t('nav.settings') }}</span>
                <span class="text-[10px] uppercase tracking-[0.2em] text-steel-400">account</span>
              </a>
              <a
                v-if="isAdmin"
                class="flex items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-medium text-steel-100 transition hover:bg-white/[0.06] hover:text-white"
                :href="resolveAppHref('/admin')"
                @click.prevent="navigateWithReload('/admin')"
              >
                <span>{{ $t('nav.admin') }}</span>
                <span class="text-[10px] uppercase tracking-[0.2em] text-steel-400">control</span>
              </a>
              <a
                class="flex items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-medium text-steel-100 transition hover:bg-white/[0.06] hover:text-white"
                :href="resolveAppHref('/report')"
                @click.prevent="navigateWithReload('/report')"
              >
                <span>{{ $t('nav.reportIssue') }}</span>
                <span class="text-[10px] uppercase tracking-[0.2em] text-steel-400">support</span>
              </a>
            </div>
            <button
              type="button"
              class="flex w-full items-center justify-center rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-200 transition hover:border-rose-400/30 hover:bg-rose-500/15 hover:text-white"
              @click="signOut"
            >
              {{ $t('nav.logout') }}
            </button>
          </div>
        </details>
      </nav>
    </header>

    <section
      v-if="showInstallBanner"
      class="border-b border-white/5 bg-midnight-900/90 px-6 py-4 backdrop-blur"
    >
      <div class="mx-auto grid max-w-6xl gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
        <div class="flex gap-4">
          <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-neon-400/30 bg-neon-400/10 text-neon-300">
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="7" y="2.5" width="10" height="19" rx="2.5" stroke-width="1.6" />
              <path d="M10.5 18.5h3" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </div>
          <div>
            <p class="font-display text-xs uppercase tracking-[0.3em] text-neon-400">{{ installBannerTitle }}</p>
            <p class="mt-1 max-w-3xl text-sm text-steel-200">
              {{ installBannerMessage }}
            </p>
            <div class="mt-3 flex flex-wrap gap-2">
              <span
                v-for="item in installBenefits"
                :key="item"
                class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-steel-300"
              >
                {{ item }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-3 lg:justify-end">
          <button
            v-if="canInstallPwa && !isIosDevice"
            type="button"
            class="rounded-xl border border-neon-400/40 bg-neon-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-neon-400 transition hover:bg-neon-400/15"
            @click="promptInstall"
          >
            {{ $t('install.installApp') }}
          </button>
          <button
            v-else
            type="button"
            class="rounded-xl border border-neon-400/40 bg-neon-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-neon-400 transition hover:bg-neon-400/15"
            @click="openInstallGuide"
          >
            {{ installGuideButtonLabel }}
          </button>
          <button
            type="button"
            class="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-steel-200 transition hover:bg-white/10 hover:text-white"
            @click="dismissInstallBanner"
          >
            {{ $t('install.notNow') }}
          </button>
        </div>
      </div>
    </section>

    <teleport to="body">
      <div
        v-if="showInstallGuide"
        class="fixed inset-0 z-[80] flex items-end bg-black/70 px-4 py-4 backdrop-blur-sm sm:items-center sm:justify-center"
        @click.self="showInstallGuide = false"
      >
        <section class="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl border border-white/10 bg-midnight-900 p-5 shadow-glow sm:p-6">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="font-display text-xs uppercase tracking-[0.3em] text-neon-400">{{ installGuideTitle }}</p>
              <h2 class="mt-2 text-2xl font-semibold text-white">{{ installGuideHeading }}</h2>
              <p class="mt-2 text-sm text-steel-300">{{ installGuideDescription }}</p>
            </div>
            <button
              type="button"
              class="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-steel-200 transition hover:bg-white/10 hover:text-white"
              :aria-label="$t('install.closeGuide')"
              @click="showInstallGuide = false"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M6 6l12 12M18 6 6 18" stroke-width="1.7" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <ol class="mt-6 grid gap-3">
            <li
              v-for="(step, index) in installGuideSteps"
              :key="step"
              class="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
            >
              <span class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-neon-400 text-sm font-semibold text-midnight-900">
                {{ index + 1 }}
              </span>
              <p class="text-sm leading-6 text-steel-200">{{ step }}</p>
            </li>
          </ol>

          <div class="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              v-if="canInstallPwa && !isIosDevice"
              type="button"
              class="inline-flex flex-1 items-center justify-center rounded-xl bg-neon-400 px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-midnight-900 transition hover:bg-neon-300"
              @click="promptInstall"
            >
              {{ $t('install.installApp') }}
            </button>
            <button
              type="button"
              class="inline-flex flex-1 items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-steel-200 transition hover:bg-white/10 hover:text-white"
              @click="dismissInstallBanner"
            >
              {{ $t('install.gotIt') }}
            </button>
          </div>
        </section>
      </div>
    </teleport>

    <main :class="mainShellClass">
      <router-view />
    </main>

    <teleport to="body">
      <div
        v-if="mobileSearchModalOpen"
        class="fixed inset-0 z-[90] bg-black/72 backdrop-blur-sm nav:hidden"
        @click.self="closeMobileSearch"
      >
        <section class="flex h-full flex-col bg-[linear-gradient(180deg,rgba(17,23,34,0.98),rgba(10,14,22,1))] px-5 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] pt-5">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="text-[10px] font-semibold uppercase tracking-[0.28em] text-neon-400">Search</p>
              <h2 class="mt-2 font-display text-2xl font-semibold text-white">Find members fast</h2>
              <p class="mt-2 text-sm text-steel-300">Search by handle or full name and jump straight into a profile.</p>
            </div>
            <button
              type="button"
              class="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-steel-200 transition hover:bg-white/10 hover:text-white"
              :aria-label="$t('install.closeGuide')"
              @click="closeMobileSearch"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M6 6l12 12M18 6 6 18" stroke-width="1.7" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <form class="mt-5" @submit.prevent="handleSearch">
            <div class="relative">
              <input
                ref="mobileSearchModalInput"
                v-model.trim="searchQuery"
                type="text"
                :placeholder="$t('app.searchPlaceholder')"
                class="w-full rounded-[1.6rem] border border-white/10 bg-white/[0.04] px-5 py-4 pr-14 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                @focus="searchActive = true"
                @input="queueSearch"
              />
              <div class="pointer-events-none absolute inset-y-0 right-5 flex items-center text-steel-400">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="6.5" stroke-width="1.8" />
                  <path d="m16 16 4.5 4.5" stroke-width="1.8" stroke-linecap="round" />
                </svg>
              </div>
            </div>
          </form>

          <div class="mt-5 flex-1 overflow-y-auto">
            <div v-if="searchQuery" class="space-y-3">
              <button
                v-for="user in searchResults"
                :key="user.handle"
                type="button"
                class="flex w-full items-center gap-4 rounded-[1.5rem] border border-white/10 bg-white/[0.04] px-4 py-3 text-left transition hover:border-white/20 hover:bg-white/[0.07]"
                @click="goToUser(user.handle)"
              >
                <div class="h-12 w-12 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    alt="User avatar"
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                    {{ (user.fullName || 'D').split(' ').filter(Boolean).slice(0, 2).map((chunk) => chunk[0].toUpperCase()).join('') }}
                  </div>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5">
                    <p class="truncate text-sm font-semibold text-white">{{ user.fullName || 'DRVN Member' }}</p>
                    <verified-badge v-if="user.verified" size="xs" />
                  </div>
                  <p class="mt-1 text-[11px] uppercase tracking-[0.22em] text-steel-400">@{{ user.handle }}</p>
                </div>
              </button>
              <p v-if="!searchResults.length && !searching" class="rounded-[1.5rem] border border-dashed border-white/10 px-4 py-5 text-sm text-steel-400">
                {{ $t('app.noUserFound') }}
              </p>
              <p v-if="searching" class="rounded-[1.5rem] border border-white/10 px-4 py-5 text-sm text-steel-400">
                {{ $t('app.searching') }}
              </p>
            </div>
            <div v-else class="rounded-[1.75rem] border border-white/10 bg-white/[0.03] p-5">
              <p class="text-[10px] uppercase tracking-[0.28em] text-steel-400">Quick tip</p>
              <p class="mt-3 text-sm leading-6 text-steel-300">Search works on handles and full names. Try `@username`, first name, or full build owner name.</p>
            </div>
          </div>
        </section>
      </div>
    </teleport>

    <nav
      v-if="showMobileTabbar"
      class="fixed inset-x-0 bottom-0 z-[60] px-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] pt-2 nav:hidden"
    >
      <div class="mx-auto grid max-w-sm grid-cols-5 gap-1 rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(23,28,39,0.96),rgba(14,18,28,0.98))] p-2 shadow-[0_-12px_40px_rgba(0,0,0,0.38)] backdrop-blur-xl">
        <a :class="mobileTabbarLinkClass('/feed')" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
          <span class="sr-only">{{ $t('nav.feed') }}</span>
          <svg class="h-[1.1rem] w-[1.1rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 12.5 12 4l9 8.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M6.5 10.5V20h11V10.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
        <button type="button" :class="mobileTabbarButtonClass(mobileSearchModalOpen)" @click="openMobileSearch">
          <span class="sr-only">Search</span>
          <svg class="h-[1.1rem] w-[1.1rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="6.5" stroke-width="1.8" />
            <path d="m16 16 4.5 4.5" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </button>
        <a :class="mobileTabbarLinkClass('/meets')" :href="resolveAppHref('/meets')" @click.prevent="navigateWithReload('/meets')">
          <span class="sr-only">{{ $t('nav.meets') }}</span>
          <svg class="h-[1.1rem] w-[1.1rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M8 3v3M16 3v3M4 9h16" stroke-width="1.8" stroke-linecap="round" />
            <rect x="4" y="5.5" width="16" height="14.5" rx="3" stroke-width="1.8" />
          </svg>
        </a>
        <a :class="mobileTabbarLinkClass('/messages')" :href="resolveAppHref('/messages')" @click.prevent="navigateWithReload('/messages')">
          <span class="sr-only">{{ $t('nav.messages') }}</span>
          <span class="relative inline-flex">
            <svg class="h-[1.1rem] w-[1.1rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M5 6.5h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" stroke-width="1.8" stroke-linejoin="round" />
            </svg>
            <span
              v-if="messageUnreadCount"
              class="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-neon-400 px-1 text-[9px] font-semibold text-midnight-900"
            >
              {{ messageUnreadCount > 9 ? '9+' : messageUnreadCount }}
            </span>
          </span>
        </a>
        <a :class="mobileTabbarLinkClass(`/u/${publicProfileHandle}`)" :href="resolveAppHref(`/u/${publicProfileHandle}`)" @click.prevent="navigateWithReload(`/u/${publicProfileHandle}`)">
          <span class="sr-only">{{ $t('nav.profile') }}</span>
          <span class="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border border-current/20 bg-midnight-900/70">
            <img
              v-if="displayAvatarUrl"
              :src="displayAvatarUrl"
              alt="Profile avatar"
              class="h-full w-full object-cover"
              referrerpolicy="no-referrer"
              crossorigin="anonymous"
            />
            <span v-else class="text-[11px] font-semibold uppercase">{{ initials }}</span>
          </span>
        </a>
      </div>
    </nav>

    <cookie-consent />
    <div
      v-if="showPushBanner"
      class="fixed inset-x-4 bottom-24 z-[135] rounded-[1.75rem] border border-white/10 bg-midnight-900/95 p-4 shadow-glow backdrop-blur sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[25rem]"
    >
      <div class="flex items-start gap-3">
        <div class="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-neon-400/30 bg-neon-400/10 text-neon-300">
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M9.5 18.5a2.5 2.5 0 0 0 5 0" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </div>
        <div class="min-w-0 flex-1">
          <p class="font-display text-xs uppercase tracking-[0.3em] text-neon-400">{{ $t('app.pushBannerEyebrow') }}</p>
          <p class="mt-2 text-sm font-semibold text-white">{{ $t('app.pushBannerTitle') }}</p>
          <p class="mt-2 text-sm text-steel-300">{{ pushBannerMessage }}</p>
          <p v-if="pushBannerError" class="mt-2 text-sm text-rose-200">{{ pushBannerError }}</p>
          <div class="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="pushPromptBusy"
              @click="enablePushFromBanner"
            >
              {{ pushPromptBusy ? $t('app.pushBannerEnabling') : $t('app.pushBannerEnable') }}
            </button>
            <button
              type="button"
              class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
              @click="dismissPushBanner"
            >
              {{ $t('app.pushBannerDismiss') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <app-toast-stack :toasts="toasts" @dismiss="dismissToast" />

  </div>
</template>

<script>
import { supabase, getAvatarUrl, getReadySession, refreshReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'
import { APP_TOAST_EVENT, emitAppToast } from '@/lib/toast'
import { getPushPermissionState, hasPushSubscription, isPushSupported, requestPushPermissionAndSubscribe, syncPushSubscription } from '@/lib/push'
import AppToastStack from '@/components/AppToastStack.vue'
import CookieConsent from '@/components/CookieConsent.vue'
import VerifiedBadge from '@/components/VerifiedBadge.vue'

export default {
  name: 'App',
  components: {
    AppToastStack,
    CookieConsent,
    VerifiedBadge,
  },
  data() {
    return {
      session: null,
      unreadCount: 0,
      messageUnreadCount: 0,
      searchQuery: '',
      searchResults: [],
      searching: false,
      searchActive: false,
      mobileSearchModalOpen: false,
      searchTimer: null,
      isNavOpen: false,
      accountMenuOpen: false,
      profile: {},
      showTransientBadge: false,
      transientBadgeTimer: null,
      installPromptEvent: null,
      canInstallPwa: false,
      showInstallGuide: false,
      isMobileDevice: false,
      profileUpdateHandler: null,
      isLightTheme: false,
      messageChannel: null,
      notificationChannel: null,
      authSubscription: null,
      standaloneDisplayModeMedia: null,
      standaloneChangeHandler: null,
      beforeInstallPromptHandler: null,
      appInstalledHandler: null,
      serviceWorkerMessageHandler: null,
      dismissedInstallBanner: false,
      toasts: [],
      toastListener: null,
      resumeHandler: null,
      isRecoveringAfterResume: false,
      pushPromptBusy: false,
      pushPromptDismissed: false,
      pushBannerError: '',
      pushPermissionState: 'default',
      hasGrantedPushSubscription: false,
    }
  },
  computed: {
    appShellClass() {
      return [
        'min-h-screen bg-midnight-900 text-steel-100 transition-colors duration-300',
        this.isLightTheme ? 'theme-light' : 'theme-dark',
      ]
    },
    mainShellClass() {
      return this.showMobileTabbar ? 'pb-28 sm:pb-0' : ''
    },
    isAuthRoute() {
      return Boolean(this.$route?.meta?.auth)
    },
    isAuthenticated() {
      return Boolean(this.session?.user)
    },
    currentRoutePath() {
      return this.$route?.path || '/'
    },
    isAdmin() {
      return this.profile?.role === 'admin'
    },
    showSearchResults() {
      return Boolean(this.searchActive && this.searchQuery)
    },
    initials() {
      const full = this.profile?.full_name || this.session?.user?.user_metadata?.full_name || ''
      if (!full) return 'D'
      return full
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join('')
    },
    displayAvatarUrl() {
      const avatarPath = this.profile?.avatar_path || this.session?.user?.user_metadata?.avatar_path || ''
      const avatarUrl = this.profile?.avatar_url || this.session?.user?.user_metadata?.avatar_url || ''
      return getAvatarUrl({ avatarPath, avatarUrl })
    },
    publicProfileHandle() {
      const handle = this.profile?.handle || this.session?.user?.user_metadata?.handle || ''
      if (handle) return handle
      const userId = this.session?.user?.id || ''
      return userId ? userId.slice(0, 8) : 'member'
    },
    isIosDevice() {
      if (typeof navigator === 'undefined') return false
      return /iPhone|iPad|iPod/i.test(navigator.userAgent || '')
    },
    isAndroidDevice() {
      if (typeof navigator === 'undefined') return false
      return /Android/i.test(navigator.userAgent || '')
    },
    isRunningStandalone() {
      if (typeof window === 'undefined') return false
      const standaloneMedia = window.matchMedia?.('(display-mode: standalone)').matches
      const standaloneNavigator = window.navigator?.standalone === true
      return Boolean(standaloneMedia || standaloneNavigator)
    },
    installBannerTitle() {
      if (this.isIosDevice) return this.$t('install.iosTitle')
      if (this.isAndroidDevice) return this.$t('install.androidTitle')
      return this.$t('install.title')
    },
    installBannerMessage() {
      if (this.isIosDevice) {
        return this.$t('install.iosMessage')
      }
      if (this.isAndroidDevice) {
        return this.canInstallPwa
          ? this.$t('install.androidMessageReady')
          : this.$t('install.androidMessageFallback')
      }
      return this.$t('install.message')
    },
    installBannerHint() {
      if (this.isIosDevice) return this.$t('install.iosHint')
      if (this.isAndroidDevice) {
        return this.canInstallPwa
          ? this.$t('install.androidHintReady')
          : this.$t('install.androidHintFallback')
      }
      return this.$t('install.hint')
    },
    installBenefits() {
      return [
        this.$t('install.benefitFast'),
        this.$t('install.benefitFullscreen'),
        this.$t('install.benefitHome'),
      ]
    },
    installGuideButtonLabel() {
      if (this.isIosDevice) return this.$t('install.showIosSteps')
      return this.$t('install.showSteps')
    },
    installGuideTitle() {
      if (this.isIosDevice) return this.$t('install.iosTitle')
      if (this.isAndroidDevice) return this.$t('install.androidTitle')
      return this.$t('install.title')
    },
    installGuideHeading() {
      if (this.isIosDevice) return this.$t('install.iosGuideHeading')
      if (this.isAndroidDevice) return this.$t('install.androidGuideHeading')
      return this.$t('install.desktopGuideHeading')
    },
    installGuideDescription() {
      if (this.isIosDevice) return this.$t('install.iosGuideDescription')
      if (this.isAndroidDevice) return this.$t('install.androidGuideDescription')
      return this.$t('install.desktopGuideDescription')
    },
    installGuideSteps() {
      if (this.isIosDevice) {
        return [
          this.$t('install.iosStep1'),
          this.$t('install.iosStep2'),
          this.$t('install.iosStep3'),
          this.$t('install.iosStep4'),
        ]
      }

      if (this.isAndroidDevice) {
        return this.canInstallPwa
          ? [this.$t('install.androidStepReady1'), this.$t('install.androidStepReady2')]
          : [this.$t('install.androidStepFallback1'), this.$t('install.androidStepFallback2'), this.$t('install.androidStepFallback3')]
      }

      return [
        this.$t('install.desktopStep1'),
        this.$t('install.desktopStep2'),
      ]
    },
    showInstallBanner() {
      if (this.dismissedInstallBanner || this.isRunningStandalone) return false
      if (this.isIosDevice) return true
      if (this.isAndroidDevice) return true
      return this.canInstallPwa
    },
    showMobileTabbar() {
      return Boolean(this.isAuthenticated && !this.isAuthRoute)
    },
    showPushBanner() {
      if (!this.isAuthenticated || this.isAuthRoute) return false
      if (!isPushSupported()) return false
      if (this.pushPromptDismissed) return false
      if (this.pushPromptBusy) return true
      return this.pushBannerEligible
    },
    pushBannerEligible() {
      return Boolean(this.session?.user && !this.hasGrantedPushSubscription && this.pushPermissionState !== 'denied')
    },
    pushBannerMessage() {
      if (this.pushPermissionState === 'granted') {
        return this.$t('app.pushBannerMessageGranted')
      }
      return this.$t('app.pushBannerMessageDefault')
    },
  },
  watch: {
    $route() {
      this.accountMenuOpen = false
      this.isNavOpen = false
      this.searchActive = false
      this.mobileSearchModalOpen = false
      this.searchQuery = ''
      this.searchResults = []
      this.refreshPushPromptState()
    },
  },
  async mounted() {
    this.loadThemePreference()
    if (typeof window !== 'undefined') {
      this.dismissedInstallBanner = window.localStorage.getItem('drvn-dismissed-install-banner') === '1'
      this.pushPromptDismissed = window.localStorage.getItem('drvn-push-banner-dismissed') === '1'
    }
    this.session = await getReadySession()
    // Ensure a profiles row exists for OAuth sign-ins (Google, etc.)
    await this.ensureProfile(this.session)
    await this.refreshNotificationCount()
    await this.refreshMessageUnreadCount()
    await this.fetchProfile()
    this.subscribeMessageUnreadCount()
    await this.initializePushNotifications()
    this.subscribeNotificationActivity()
    await this.refreshPushPromptState()

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      this.session = session
      // Create/upsert profiles row when a user signs in via OAuth
      await this.ensureProfile(session)
      this.refreshNotificationCount()
      this.refreshMessageUnreadCount()
      if (session?.user?.id) {
        this.subscribeMessageUnreadCount()
        await this.initializePushNotifications()
        this.subscribeNotificationActivity()
        this.pushPromptDismissed = false
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('drvn-push-banner-dismissed')
        }
      } else if (this.messageChannel) {
        supabase.removeChannel(this.messageChannel)
        this.messageChannel = null
        this.teardownNotificationActivity()
      }
      this.fetchProfile()
      await this.refreshPushPromptState()
      if (event === 'SIGNED_OUT' && this.$route?.meta?.requiresAuth) {
        this.$router.replace('/login')
      }
    })
    this.authSubscription = authListener?.subscription || null

    const ua = navigator.userAgent || ''
    this.isMobileDevice = /Android|iPhone|iPad|iPod/i.test(ua)

    this.standaloneDisplayModeMedia = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null
    this.standaloneChangeHandler = () => this.handleStandaloneChange()
    if (this.standaloneDisplayModeMedia?.addEventListener) {
      this.standaloneDisplayModeMedia.addEventListener('change', this.standaloneChangeHandler)
    } else if (this.standaloneDisplayModeMedia?.addListener) {
      this.standaloneDisplayModeMedia.addListener(this.standaloneChangeHandler)
    }

    this.beforeInstallPromptHandler = (event) => {
      event.preventDefault()
      this.installPromptEvent = event
      this.canInstallPwa = true
      window.__drvnInstallPrompt = event
    }
    window.addEventListener('beforeinstallprompt', this.beforeInstallPromptHandler)

    this.appInstalledHandler = () => {
      this.installPromptEvent = null
      this.canInstallPwa = false
      window.__drvnInstallPrompt = null
      this.dismissInstallBanner()
    }
    window.addEventListener('appinstalled', this.appInstalledHandler)

    this.profileUpdateHandler = (event) => {
      const detail = event?.detail || {}
      if (detail.avatar_path || detail.avatar_url) {
        this.profile = {
          ...this.profile,
          avatar_path: detail.avatar_path ?? this.profile?.avatar_path,
          avatar_url: detail.avatar_url ?? this.profile?.avatar_url,
        }
        return
      }
      this.fetchProfile()
    }
    window.addEventListener('profile:updated', this.profileUpdateHandler)
    document.addEventListener('click', this.handleDocumentClick)

    this.toastListener = (event) => {
      const detail = event?.detail || {}
      const toast = {
        id: detail.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: detail.title || '',
        message: detail.message || '',
        tone: detail.tone || 'info',
      }

      this.toasts = [...this.toasts, toast].slice(-4)
      const timeout = Number(detail.timeout || 3200)
      window.setTimeout(() => {
        this.dismissToast(toast.id)
      }, timeout)
    }
    window.addEventListener(APP_TOAST_EVENT, this.toastListener)

    this.serviceWorkerMessageHandler = (event) => {
      const payload = event?.data?.payload
      if (event?.data?.type !== 'DRVN_PUSH_WHILE_ACTIVE' || !payload) return

      emitAppToast({
        title: payload.title || this.$t('app.newActivity'),
        message: payload.body || this.$t('app.openDrvn'),
        tone: 'info',
        timeout: 4200,
      })
    }
    navigator.serviceWorker?.addEventListener?.('message', this.serviceWorkerMessageHandler)

    this.resumeHandler = () => {
      this.recoverAfterResume()
    }
    document.addEventListener('visibilitychange', this.resumeHandler)
    window.addEventListener('focus', this.resumeHandler)
    window.addEventListener('online', this.resumeHandler)
    window.addEventListener('pageshow', this.resumeHandler)
  },
  beforeUnmount() {
    if (this.transientBadgeTimer) {
      clearTimeout(this.transientBadgeTimer)
      this.transientBadgeTimer = null
    }
    if (this.profileUpdateHandler) {
      window.removeEventListener('profile:updated', this.profileUpdateHandler)
      this.profileUpdateHandler = null
    }
    document.removeEventListener('click', this.handleDocumentClick)
    if (this.messageChannel) {
      supabase.removeChannel(this.messageChannel)
      this.messageChannel = null
    }
    this.teardownNotificationActivity()
    if (this.authSubscription) {
      this.authSubscription.unsubscribe()
      this.authSubscription = null
    }
    if (this.beforeInstallPromptHandler) {
      window.removeEventListener('beforeinstallprompt', this.beforeInstallPromptHandler)
      this.beforeInstallPromptHandler = null
    }
    if (this.appInstalledHandler) {
      window.removeEventListener('appinstalled', this.appInstalledHandler)
      this.appInstalledHandler = null
    }
    if (this.standaloneDisplayModeMedia && this.standaloneChangeHandler) {
      if (this.standaloneDisplayModeMedia.removeEventListener) {
        this.standaloneDisplayModeMedia.removeEventListener('change', this.standaloneChangeHandler)
      } else if (this.standaloneDisplayModeMedia.removeListener) {
        this.standaloneDisplayModeMedia.removeListener(this.standaloneChangeHandler)
      }
    }
    this.standaloneDisplayModeMedia = null
    this.standaloneChangeHandler = null
    if (this.toastListener) {
      window.removeEventListener(APP_TOAST_EVENT, this.toastListener)
      this.toastListener = null
    }
    if (this.serviceWorkerMessageHandler) {
      navigator.serviceWorker?.removeEventListener?.('message', this.serviceWorkerMessageHandler)
      this.serviceWorkerMessageHandler = null
    }
    if (this.resumeHandler) {
      document.removeEventListener('visibilitychange', this.resumeHandler)
      window.removeEventListener('focus', this.resumeHandler)
      window.removeEventListener('online', this.resumeHandler)
      window.removeEventListener('pageshow', this.resumeHandler)
      this.resumeHandler = null
    }
  },
  methods: {
    isRouteActive(target) {
      if (!target) return false
      if (target === '/') return this.currentRoutePath === '/'
      return this.currentRoutePath === target || this.currentRoutePath.startsWith(`${target}/`)
    },
    desktopNavLinkClass(target) {
      return [
        'inline-flex items-center gap-2 rounded-full px-3 py-2 text-[11px] font-semibold tracking-[0.18em] transition',
        this.isRouteActive(target)
          ? 'bg-white text-midnight-900 shadow-[0_8px_24px_rgba(255,255,255,0.14)]'
          : 'text-steel-300 hover:bg-white/8 hover:text-white',
      ]
    },
    mobileNavLinkClass(target) {
      return [
        'flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition',
        this.isRouteActive(target)
          ? 'border-white/20 bg-white text-midnight-900 shadow-[0_14px_34px_rgba(255,255,255,0.14)]'
          : 'border-white/10 bg-white/[0.03] text-steel-100 hover:border-white/20 hover:bg-white/[0.06]',
      ]
    },
    iconActionClass(target) {
      return [
        'relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border transition',
        this.isRouteActive(target)
          ? 'border-white/20 bg-white text-midnight-900 shadow-[0_10px_30px_rgba(255,255,255,0.16)]'
          : 'border-white/10 bg-white/[0.03] text-steel-300 hover:border-white/20 hover:bg-white/10 hover:text-white',
      ]
    },
    mobileTabbarLinkClass(target) {
      return [
        'flex min-h-[3.5rem] flex-col items-center justify-center gap-1 rounded-[1.2rem] px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] transition',
        this.isRouteActive(target)
          ? 'bg-white text-midnight-900 shadow-[0_10px_26px_rgba(255,255,255,0.16)]'
          : 'text-steel-300 hover:bg-white/8 hover:text-white',
      ]
    },
    mobileTabbarButtonClass(isActive = false) {
      return [
        'flex min-h-[3.5rem] flex-col items-center justify-center gap-1 rounded-[1.2rem] px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] transition',
        isActive
          ? 'bg-white text-midnight-900 shadow-[0_10px_26px_rgba(255,255,255,0.16)]'
          : 'text-steel-300 hover:bg-white/8 hover:text-white',
      ]
    },
    resolveAppHref(to) {
      return this.$router.resolve(to).href
    },
    navigateWithReload(to) {
      this.accountMenuOpen = false
      this.isNavOpen = false
      this.searchActive = false
      this.searchQuery = ''
      this.searchResults = []

      const targetRoute = this.$router.resolve(to)
      if (targetRoute.fullPath === this.$route.fullPath) {
        // window.location.reload()
        return
      }

      // window.location.assign(targetRoute.href)
      this.$router.push(to)
    },
    async markNotificationsAsRead() {
      const userId = this.session?.user?.id
      this.unreadCount = 0
      if (this.transientBadgeTimer) {
        clearTimeout(this.transientBadgeTimer)
        this.transientBadgeTimer = null
      }
      this.showTransientBadge = false
      if (!userId) return

      try {
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('recipient_id', userId)
          .eq('is_read', false)
      } catch (_error) {
        // Keep the UI responsive even if marking read fails.
      }
    },
    async openNotifications() {
      await this.markNotificationsAsRead()
      this.navigateWithReload('/notifications')
    },
    openMobileSearch() {
      this.accountMenuOpen = false
      this.isNavOpen = false
      this.mobileSearchModalOpen = true
      this.searchActive = true
      this.$nextTick(() => {
        this.$refs.mobileSearchModalInput?.focus?.()
      })
    },
    closeMobileSearch() {
      this.mobileSearchModalOpen = false
      this.searchActive = false
      this.searchQuery = ''
      this.searchResults = []
      this.searching = false
    },
    openMobileComposer() {
      this.accountMenuOpen = false
      this.isNavOpen = false
      this.searchActive = false
      this.mobileSearchModalOpen = false
      this.searchQuery = ''
      this.searchResults = []
      const targetRoute = this.$router.resolve({ path: '/feed', query: { compose: '1' } })
      window.location.assign(targetRoute.href)
    },
    dismissToast(toastId) {
      this.toasts = this.toasts.filter((toast) => toast.id !== toastId)
    },
    async refreshPushPromptState() {
      this.pushBannerError = ''
      this.pushPermissionState = getPushPermissionState()
      this.hasGrantedPushSubscription = this.pushPermissionState === 'granted' && await hasPushSubscription()
    },
    loadThemePreference() {
      if (typeof window === 'undefined') return
      const savedTheme = window.localStorage.getItem('drvn-theme')
      this.isLightTheme = savedTheme === 'light'
      this.applyTheme()
    },
    applyTheme() {
      if (typeof document === 'undefined') return
      document.documentElement.classList.toggle('theme-light', this.isLightTheme)
      document.body.classList.toggle('theme-light', this.isLightTheme)
      this.syncThemeMeta()
    },
    syncThemeMeta() {
      if (typeof document === 'undefined') return
      const themeColor = this.isLightTheme ? '#f5f7fb' : '#0b0f14'

      let themeMeta = document.querySelector('meta[name="theme-color"]')
      if (!themeMeta) {
        themeMeta = document.createElement('meta')
        themeMeta.setAttribute('name', 'theme-color')
        document.head.appendChild(themeMeta)
      }
      themeMeta.setAttribute('content', themeColor)

      let statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
      if (!statusBarMeta) {
        statusBarMeta = document.createElement('meta')
        statusBarMeta.setAttribute('name', 'apple-mobile-web-app-status-bar-style')
        document.head.appendChild(statusBarMeta)
      }
      statusBarMeta.setAttribute('content', this.isLightTheme ? 'default' : 'black-translucent')
    },
    toggleTheme() {
      this.isLightTheme = !this.isLightTheme
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('drvn-theme', this.isLightTheme ? 'light' : 'dark')
      }
      this.applyTheme()
    },
    handleStandaloneChange() {
      if (this.isRunningStandalone) {
        this.installPromptEvent = null
        this.canInstallPwa = false
      }
    },
    dismissInstallBanner() {
      this.dismissedInstallBanner = true
      this.showInstallGuide = false
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('drvn-dismissed-install-banner', '1')
      }
    },
    openInstallGuide() {
      this.showInstallGuide = true
    },
    async promptInstall() {
      if (!this.installPromptEvent) {
        this.openInstallGuide()
        return
      }
      this.installPromptEvent.prompt()
      const { outcome } = await this.installPromptEvent.userChoice
      if (outcome === 'accepted') {
        this.dismissInstallBanner()
      }
      this.showInstallGuide = false
      this.installPromptEvent = null
      this.canInstallPwa = false
      window.__drvnInstallPrompt = null
    },
    async initializePushNotifications() {
      if (!this.session?.user?.id || !isPushSupported()) return
      if (Notification.permission !== 'granted') return

      try {
        await syncPushSubscription(this.session)
        await this.refreshPushPromptState()
      } catch (_error) {
        // Push registration is best-effort.
      }
    },
    dismissPushBanner() {
      this.pushPromptDismissed = true
      this.pushBannerError = ''
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('drvn-push-banner-dismissed', '1')
      }
    },
    async enablePushFromBanner() {
      if (!this.session?.user?.id) return

      this.pushPromptBusy = true
      this.pushBannerError = ''

      try {
        const session = await refreshReadySession()
        await requestPushPermissionAndSubscribe(session)
        await this.refreshPushPromptState()
        this.pushPromptDismissed = true
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('drvn-push-banner-dismissed', '1')
        }
        emitAppToast({
          title: 'Push enabled',
          message: 'This device will now receive DRVN notifications.',
          tone: 'success',
        })
      } catch (error) {
        this.pushBannerError = error.message || 'Unable to enable notifications on this device.'
        await this.refreshPushPromptState()
      } finally {
        this.pushPromptBusy = false
      }
    },
    async recoverAfterResume() {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      if (this.isRecoveringAfterResume) return

      this.isRecoveringAfterResume = true
      try {
        supabase.auth?.startAutoRefresh?.()
        supabase.realtime?.connect?.()

        this.session = await refreshReadySession()
        await this.ensureProfile(this.session)
        await Promise.all([
          this.refreshNotificationCount(),
          this.refreshMessageUnreadCount(),
          this.fetchProfile(),
        ])

        if (this.session?.user?.id) {
          this.subscribeMessageUnreadCount()
          this.subscribeNotificationActivity()
          await this.initializePushNotifications()
        } else {
          if (this.messageChannel) {
            supabase.removeChannel(this.messageChannel)
            this.messageChannel = null
          }
          this.teardownNotificationActivity()
        }

        window.dispatchEvent(new CustomEvent('drvn:resume', { detail: { session: this.session } }))
      } finally {
        this.isRecoveringAfterResume = false
      }
    },
    async refreshNotificationCount() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.unreadCount = 0
        return
      }

      const { count } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('is_read', false)

      this.unreadCount = count || 0

      // transient pop badge: show briefly for 5s when there are unread notifications
      if (this.unreadCount > 0) {
        this.showTransientBadge = true
        if (this.transientBadgeTimer) clearTimeout(this.transientBadgeTimer)
        this.transientBadgeTimer = setTimeout(() => {
          this.showTransientBadge = false
          this.transientBadgeTimer = null
        }, 5000)
      } else {
        if (this.transientBadgeTimer) {
          clearTimeout(this.transientBadgeTimer)
          this.transientBadgeTimer = null
        }
        this.showTransientBadge = false
      }
    },
    async refreshMessageUnreadCount() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.messageUnreadCount = 0
        return
      }

      const { count } = await supabase
        .from('direct_messages')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .is('seen_at', null)

      this.messageUnreadCount = count || 0
    },
    subscribeMessageUnreadCount() {
      if (!this.session?.user?.id) return
      if (this.messageChannel) {
        supabase.removeChannel(this.messageChannel)
      }

      this.messageChannel = supabase
        .channel(`app-direct-messages-${this.session.user.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'direct_messages' }, async () => {
          await this.refreshMessageUnreadCount()
        })
        .subscribe()
    },
    teardownNotificationActivity() {
      if (this.notificationChannel) {
        supabase.removeChannel(this.notificationChannel)
        this.notificationChannel = null
      }
    },
    subscribeNotificationActivity() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.teardownNotificationActivity()
        return
      }

      this.teardownNotificationActivity()
      this.notificationChannel = supabase
        .channel(`app-notifications-${userId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`,
        }, async (payload) => {
          await this.refreshNotificationCount()
          await this.showIncomingNotificationToast(payload?.new)
        })
        .subscribe()
    },
    getNotificationToastMeta(type = '', actor = {}) {
      const actorName = actor.full_name || actor.handle || 'Someone'
      const actorHandle = actor.handle ? `@${actor.handle}` : actorName

      if (type === 'post_like') return { title: 'New like', message: `${actorHandle} liked your post.`, tone: 'success' }
      if (type === 'post_save') return { title: 'Post saved', message: `${actorHandle} saved your build.`, tone: 'success' }
      if (type === 'post_comment') return { title: 'New comment', message: `${actorHandle} commented on your post.`, tone: 'info' }
      if (type === 'comment_reply') return { title: 'New reply', message: `${actorHandle} replied to your comment.`, tone: 'info' }
      if (type === 'comment_like') return { title: 'Comment liked', message: `${actorHandle} liked your comment.`, tone: 'success' }
      if (type === 'follow') return { title: 'New follower', message: `${actorName} started following you.`, tone: 'success' }
      if (String(type).startsWith('post_reaction_')) {
        const reactionTone = String(type).replace('post_reaction_', '').replaceAll('_', ' ')
        return { title: 'New reaction', message: `${actorHandle} reacted ${reactionTone} to your post.`, tone: 'info' }
      }

      return { title: 'New activity', message: `${actorHandle} interacted with you.`, tone: 'info' }
    },
    async showIncomingNotificationToast(notification) {
      if (!notification?.actor_id) return

      const { data: actor } = await supabase
        .from('profiles')
        .select('handle, full_name')
        .eq('id', notification.actor_id)
        .maybeSingle()

      const toast = this.getNotificationToastMeta(notification.type, actor || {})
      emitAppToast({ ...toast, timeout: 4200 })
    },
    async fetchProfile() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.profile = {}
        return
      }
      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('profiles')
          .select('handle, full_name, avatar_url, avatar_path, role, status')
          .eq('id', userId)
          .maybeSingle()
      ))
      if (!error && data) {
        this.profile = data
        if (data.status === 'banned') {
          await supabase.auth.signOut()
          this.session = null
          this.$router.push('/login?banned=1')
        }
      }
    },

    async ensureProfile(session) {
      try {
        const user = session?.user
        if (!user) return

        const userId = user.id
        // check if profile exists
        const { data: existing } = await supabase
          .from('profiles')
          .select('id, avatar_url')
          .eq('id', userId)
          .maybeSingle()

        // derive fields from user metadata or fallback to email prefix / id
        const meta = user.user_metadata || {}
        const handleFromMeta = (meta.handle && String(meta.handle).toLowerCase()) || ''
        const emailHandle = user.email ? String(user.email).split('@')[0].toLowerCase() : ''
        const handle = handleFromMeta || emailHandle || userId.slice(0, 8)
        const full_name = meta.full_name || meta.name || ''
        const avatar_url = meta.avatar_url || meta.picture || ''

        if (existing) {
          if (avatar_url && !existing.avatar_url) {
            await supabase.from('profiles').update({ avatar_url }).eq('id', userId)
          }
          return
        }

        await supabase
          .from('profiles')
          .upsert(
            {
              id: userId,
              handle,
              full_name,
              avatar_url,
            },
            { onConflict: 'id' }
          )
      } catch (e) {
        // ignore - profile creation is best-effort here
        console.warn('ensureProfile failed', e)
      }
    },
    handleDocumentClick(event) {
      const target = event?.target
      if (!(target instanceof Element)) return

      if (!target.closest('[data-account-menu-root]')) {
        this.accountMenuOpen = false
      }

      if (!this.mobileSearchModalOpen && !target.closest('[data-search-root]')) {
        this.searchActive = false
      }
    },
    // close the account menu on route change
    watchRoute() {
      this.accountMenuOpen = false
    },

    async signOut() {
      await supabase.auth.signOut()
      this.session = null
      this.profile = {}
      this.hasGrantedPushSubscription = false
      this.pushPermissionState = getPushPermissionState()
      this.accountMenuOpen = false
      this.isNavOpen = false
      await this.$router.replace('/login')
    },
    handleSearch() {
      if (!this.searchQuery) return
      if (this.searchResults.length) {
        this.goToUser(this.searchResults[0].handle)
        return
      }
      const handle = this.searchQuery.replace(/^@/, '')
      this.$router.push(`/u/${handle}`)
      this.searchQuery = ''
      this.searchActive = false
      this.mobileSearchModalOpen = false
    },
    queueSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      const query = this.searchQuery
      if (!query) {
        this.searchResults = []
        this.searching = false
        this.searchActive = false
        return
      }
      this.searchActive = true
      this.searching = true
      this.searchTimer = setTimeout(() => {
        this.fetchSearchResults(query)
      }, 250)
    },
    async fetchSearchResults(query) {
      const cleaned = query.replace(/^@/, '')
      const { data, error } = await supabase
        .from('profiles')
        .select('handle, full_name, avatar_url, avatar_path, verified')
        .or(`handle.ilike.%${cleaned}%,full_name.ilike.%${cleaned}%`)
        .limit(5)

      if (error) {
        this.searchResults = []
        this.searching = false
        return
      }

      this.searchResults = (data || []).map((user) => {
        const avatar = getAvatarUrl({ avatarPath: user.avatar_path, avatarUrl: user.avatar_url })
        return {
          handle: user.handle,
          fullName: user.full_name,
          avatar,
          verified: Boolean(user.verified),
        }
      })
      this.searching = false
    },
    goToUser(handle) {
      this.$router.push(`/u/${handle}`)
      this.searchQuery = ''
      this.searchResults = []
      this.searching = false
      this.searchActive = false
      this.mobileSearchModalOpen = false
    },
  },
}
</script>

<style scoped>
.pop-animate {
  animation: pop 0.8s ease-in-out infinite;
  transform-origin: center;
}
@keyframes pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.25); }
  100% { transform: scale(1); }
}
</style>
